import React, { useState, useMemo } from "react";
import {
    Box,
    Paper,
    Typography,
    Grid,
    Button,
    FormControl,
    InputLabel,
    Select,
    MenuItem,
    TextField,
    Checkbox,
    ListItemText,
    OutlinedInput,
    Chip,
    IconButton,
    TextField as MuiTextField,
    InputAdornment,
} from "@mui/material";
import SearchIcon from "@mui/icons-material/Search";
import EditIcon from "@mui/icons-material/Edit";
import EnhancedTable from "../../../components/common/EnhancedTable";
import {
    format,
    startOfMonth,
    endOfMonth,
    eachDayOfInterval,
    isSameDay,
    startOfDay,
} from "date-fns";
import { useTranslation } from "react-i18next";
import { useSessions } from "../../../hooks/useSession";
import { useAuth } from "../../../hooks/useAuth";
import { useClasses } from "../../../hooks/useClasses";
import { useHolidays } from "../../../hooks/useHolidays";
import { useSessionClass } from "../../../contexts/SessionClassContext";
import CalendarMonthIcon from "@mui/icons-material/CalendarMonth";
import ChevronLeftIcon from "@mui/icons-material/ChevronLeft";
import ChevronRightIcon from "@mui/icons-material/ChevronRight";
import CommonModal from '../../../components/common/CommonModal';

const HolidayCalendarManagement: React.FC = () => {
    const { t } = useTranslation();
    const { user } = useAuth();
    const schoolId = user?.schoolId;

    const { sessionsQuery } = useSessions(schoolId ?? undefined);
    const { selectedSession } = useSessionClass();
    const selectedSessionId = selectedSession?.id || "";

    // Visibility state
    const [calendarOpen, setCalendarOpen] = useState(false);

    const [currentMonth, setCurrentMonth] = useState(new Date().getMonth());
    const [currentYear, setCurrentYear] = useState(new Date().getFullYear());
    const [selectionMode, setSelectionMode] = useState<"single" | "range">("single");
    const [confirmModeChangeOpen, setConfirmModeChangeOpen] = useState(false);
    const [pendingSelectionMode, setPendingSelectionMode] = useState<"single" | "range" | null>(null);

    // Converts backend enum names like EXAM_BREAK -> "Exam Break"
    const formatHolidayType = (name: string): string =>
        name
            .split("_")
            .map(word => word.charAt(0).toUpperCase() + word.slice(1).toUpperCase())
            .join(" ");

    const [selectedDates, setSelectedDates] = useState<Date[]>([]);
    const [rangeStart, setRangeStart] = useState<Date | null>(null);
    const [rangeEnd, setRangeEnd] = useState<Date | null>(null);

    // Dialog state
    const [dialogOpen, setDialogOpen] = useState(false);
    const [holidayType, setHolidayType] = useState<number | "">("");
    const [description, setDescription] = useState("");
    const [classOption, setClassOption] = useState("all");
    const [selectedClasses, setSelectedClasses] = useState<number[]>([]);
    const [holidaySubmitError, setHolidaySubmitError] = useState<string>("");

    const { classesQuery } = useClasses(schoolId ?? undefined);
    const {
        useholidaysQueryBySession,
        createHolidayMutation,
        deleteHolidayMutation,
        activeHolidayTypesQuery
    } = useHolidays(Number(selectedSessionId));

    const [mockHolidayExists, setMockHolidayExists] = useState(false);
    const [deselectedExistingDates, setDeselectedExistingDates] = useState<string[]>([]);
    const [editingHolidayId, setEditingHolidayId] = useState<number | null>(null);

    // Reset mock holiday state when switching sessions
    React.useEffect(() => {
        setMockHolidayExists(false);
    }, [selectedSessionId]);

    const holidayExists = useMemo(() =>
        mockHolidayExists || (useholidaysQueryBySession.data && useholidaysQueryBySession.data.length > 0),
        [mockHolidayExists, useholidaysQueryBySession.data]
    );


    const sessionYears = useMemo(() => {
        if (!selectedSession) return [new Date().getFullYear()];
        const years = selectedSession.name.split("-").map(y => parseInt(y.trim()));
        if (years.length === 2) {
            let y1 = years[0];
            let y2 = years[1];
            if (y2 < 100) y2 = Math.floor(y1 / 100) * 100 + y2;
            return [y1, y2];
        }
        return [years[0]];
    }, [selectedSession]);

    // Update currentYear when session changes
    React.useEffect(() => {
        if (sessionYears.length > 0 && !sessionYears.includes(currentYear)) {
            setCurrentYear(sessionYears[0]);
        }
    }, [sessionYears, currentYear]);

    const daysInMonth = useMemo(() => {
        const start = startOfMonth(new Date(currentYear, currentMonth));
        const end = endOfMonth(new Date(currentYear, currentMonth));
        return eachDayOfInterval({ start, end });
    }, [currentMonth, currentYear]);

    const handleDateClick = (date: Date) => {
        const normalizedDate = startOfDay(date);
        // Schools treat Sundays as weekly holidays; never allow selecting them.
        if (normalizedDate.getDay() === 0) return;
        const dateStr = format(normalizedDate, "dd-MM-yyyy");

        // If clicked on an existing holiday, toggle its "deselected" status
        const holidayFromDate = useholidaysQueryBySession.data?.find(h => h.dates.includes(dateStr));
        if (isExistingHoliday(normalizedDate) || deselectedExistingDates.includes(dateStr) || holidayFromDate) {
            if (holidayFromDate) setEditingHolidayId(holidayFromDate.id);

            if (deselectedExistingDates.includes(dateStr)) {
                setDeselectedExistingDates(deselectedExistingDates.filter(d => d !== dateStr));
            } else {
                setDeselectedExistingDates([...deselectedExistingDates, dateStr]);
            }
            return;
        }

        if (selectionMode === "single") {
            const isSelected = selectedDates.some(d => isSameDay(d, normalizedDate));
            if (isSelected) {
                setSelectedDates(selectedDates.filter(d => !isSameDay(d, normalizedDate)));
            } else {
                setSelectedDates([...selectedDates, normalizedDate]);
            }
        } else {
            if (!rangeStart || (rangeStart && rangeEnd)) {
                setRangeStart(normalizedDate);
                setRangeEnd(null);
                setSelectedDates([normalizedDate]);
            } else {
                let start = rangeStart;
                let end = normalizedDate;
                if (end < start) {
                    [start, end] = [end, start];
                }
                setRangeStart(start);
                setRangeEnd(end);

                const rangeDates = eachDayOfInterval({ start, end });
                setSelectedDates(rangeDates);
            }
        }
    };

    const isDateSelected = (date: Date) => {
        return selectedDates.some(d => isSameDay(d, date));
    };

    const isSunday = (date: Date) => startOfDay(date).getDay() === 0;

    const isExistingHoliday = (date: Date) => {
        if (!useholidaysQueryBySession.data) return false;
        const normalizedDateStr = format(date, "dd-MM-yyyy");
        // If it's in the deselected list, don't treat it as an active existing holiday
        if (deselectedExistingDates.includes(normalizedDateStr)) return false;

        return useholidaysQueryBySession.data.some(h =>
            h.dates.includes(normalizedDateStr)
        );
    };

    const handleOk = () => {
        if (selectedDates.length === 0 && deselectedExistingDates.length === 0 && !editingHolidayId) return;
        setHolidaySubmitError("");

        // If we have an editingHolidayId or deselected dates, pre-fill the dialog with the holiday's data
        let holidayIdToUse = editingHolidayId;
        if (!holidayIdToUse && deselectedExistingDates.length > 0) {
            const firstDeselectedDateStr = deselectedExistingDates[0];
            const foundHoliday = useholidaysQueryBySession.data?.find(h =>
                h.dates.includes(firstDeselectedDateStr)
            );
            if (foundHoliday) holidayIdToUse = foundHoliday.id;
        }

        if (holidayIdToUse) {
            const existingHoliday = useholidaysQueryBySession.data?.find(h => h.id === holidayIdToUse);
            if (existingHoliday) {
                setEditingHolidayId(existingHoliday.id);
                setHolidayType(existingHoliday.typeId);
                setDescription(existingHoliday.description);
                setClassOption(existingHoliday.forAllClasses ? "all" : "particular");
                setSelectedClasses(existingHoliday.classIds || []);
            }
        } else if (selectedDates.length > 0) {
            // Reset for entirely new selection
            setEditingHolidayId(null);
            setHolidayType("");
            setDescription("");
            setClassOption("all");
            setSelectedClasses([]);
        }

        setDialogOpen(true);
        setCalendarOpen(false);
    };

    const handleClassChange = (event: any) => {
        const { target: { value } } = event;

        // Handle "Select All" click
        if (value.includes("all")) {
            if (selectedClasses.length === (classesQuery.data?.length || 0)) {
                setSelectedClasses([]);
            } else {
                setSelectedClasses(classesQuery.data?.map(c => c.id!) || []);
            }
            return;
        }

        setSelectedClasses(typeof value === "string" ? value.split(",").map(Number) : value);
    };

    const extractHolidayErrorMessage = (err: any): string => {
        const serverMessage =
            err?.response?.data?.message ||
            err?.response?.data?.error ||
            err?.message;

        if (typeof serverMessage === "string" && serverMessage.trim().length > 0) {
            return serverMessage;
        }

        return t("Something went wrong. Please try again.");
    };

    const handleSubmitHoliday = async () => {
        if (selectedDates.length === 0 && deselectedExistingDates.length === 0 && !editingHolidayId) return;
        setHolidaySubmitError("");

        try {
            const mutationPromises: Promise<unknown>[] = [];

            // 1. Handle Update of Existing Holiday (Merge Case)
            if (editingHolidayId) {
                const existingHoliday = useholidaysQueryBySession.data?.find(h => h.id === editingHolidayId);
                if (existingHoliday) {
                    // Unified list: (Existing Dates - Deselected) + New Selections
                    const remainingExisting = existingHoliday.dates.filter(d => !deselectedExistingDates.includes(d));
                    const newDatesFormatted = selectedDates.map(d => format(d, "dd-MM-yyyy"));

                    // Union of both sets, ensuring uniqueness
                    const unifiedDates = Array.from(new Set([...remainingExisting, ...newDatesFormatted]));

                    if (unifiedDates.length === 0) {
                        mutationPromises.push(deleteHolidayMutation.mutateAsync(editingHolidayId));
                    } else {
                        mutationPromises.push(
                            createHolidayMutation.mutateAsync({
                                id: editingHolidayId,
                                sessionId: Number(selectedSessionId),
                                typeId: Number(holidayType),
                                dates: unifiedDates,
                                description,
                                forAllClasses: classOption === "all",
                                classIds: classOption === "particular" ? selectedClasses : [],
                            })
                        );
                    }
                }
            }
            // 2. Handle New Selection (if not merged into an existing holiday)
            else if (selectedDates.length > 0) {
                const dates = selectedDates.map(d => format(d, "dd-MM-yyyy"));
                mutationPromises.push(
                    createHolidayMutation.mutateAsync({
                        sessionId: Number(selectedSessionId),
                        typeId: Number(holidayType),
                        dates,
                        description,
                        forAllClasses: classOption === "all",
                        classIds: classOption === "particular" ? selectedClasses : [],
                    })
                );
            }

            // 3. Cleanup other deselected holidays (if any additional ones were touched)
            if (deselectedExistingDates.length > 0) {
                const removalsByHoliday: Record<number, string[]> = {};
                deselectedExistingDates.forEach(dateStr => {
                    const holiday = useholidaysQueryBySession.data?.find(h => h.dates.includes(dateStr));
                    if (holiday && holiday.id && holiday.id !== editingHolidayId) { // Skip the one already handled
                        if (!removalsByHoliday[holiday.id]) removalsByHoliday[holiday.id] = [];
                        removalsByHoliday[holiday.id].push(dateStr);
                    }
                });

                Object.entries(removalsByHoliday).forEach(([idStr, removedDates]) => {
                    const id = Number(idStr);
                    const holiday = useholidaysQueryBySession.data?.find(h => h.id === id);
                    if (holiday) {
                        const remainingDates = holiday.dates.filter(d => !removedDates.includes(d));
                        if (remainingDates.length === 0) {
                            mutationPromises.push(deleteHolidayMutation.mutateAsync(id));
                        } else {
                            mutationPromises.push(
                                createHolidayMutation.mutateAsync({
                                    id: holiday.id,
                                    sessionId: holiday.sessionId,
                                    typeId: holiday.typeId,
                                    dates: remainingDates,
                                    description: holiday.description,
                                    forAllClasses: holiday.forAllClasses,
                                    classIds: holiday.classIds,
                                })
                            );
                        }
                    }
                });
            }

            await Promise.all(mutationPromises);

            // Cleanup (only after successful API calls)
            setDialogOpen(false);
            setMockHolidayExists(true);
            setSelectedDates([]);
            setDeselectedExistingDates([]);
            setEditingHolidayId(null);
            setRangeStart(null);
            setRangeEnd(null);
            setHolidayType("");
            setDescription("");
            setClassOption("all");
            setSelectedClasses([]);
        } catch (err) {
            setHolidaySubmitError(extractHolidayErrorMessage(err));
            // Do not "stick" to the failed selection inside the modal
            setSelectedDates([]);
            setRangeStart(null);
            setRangeEnd(null);
        }
    };

    const handleClearSelection = () => {
        setSelectedDates([]);
        setDeselectedExistingDates([]);
        setRangeStart(null);
        setRangeEnd(null);
    };

    const handleOpenCalendar = () => {
        setCalendarOpen(true);
    };

    const columns = [
        { id: "name", label: t("name") },
    ];

    const tableData = useMemo(() => {
        if (!selectedSession || !holidayExists) return [];
        return [
            {
                id: 1,
                name: `${t("holiday_calendar")} (${selectedSession.name})`,
            }
        ];
    }, [selectedSession, holidayExists, t]);

    return (
        <Paper sx={{ p: 1.5, maxWidth: 1500, mx: 'auto', mt: 1 }}>
            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 1 }}>
                <Typography variant="h6" fontWeight={700} sx={{ fontSize: "1.25rem", whiteSpace: "nowrap" }}>
                    {t("holiday_calendar")}
                </Typography>
                <Button
                    variant="contained"
                    size="small"
                    startIcon={<CalendarMonthIcon />}
                    onClick={handleOpenCalendar}
                    disabled={holidayExists}
                    sx={{ backgroundColor: "#3092e3", "&:hover": { backgroundColor: "primary.main" }, height: 36 }}
                >
                    {t("holiday_calendar")}
                </Button>
            </Box>

            <EnhancedTable
                columns={columns}
                data={tableData}
                actions={(row) => (
                    <Button
                        variant="contained"
                        size="small"
                        startIcon={<EditIcon />}
                        onClick={handleOpenCalendar}
                        sx={{ backgroundColor: "#3092e3", "&:hover": { backgroundColor: "primary.main" }, height: 32 }}
                    >
                        {t("edit")}
                    </Button>
                )}
                cardProps={{
                    elevation: 0,
                    sx: {
                        border: 'none',
                        boxShadow: 'none',
                        backgroundColor: 'transparent',
                        mb: 0,
                        '& .MuiCardContent-root': { p: 0, '&:last-child': { pb: 0 } }
                    }
                }}
            />

            {/* Big Calendar Form (Dialog) */}
            <CommonModal
                open={calendarOpen}
                onClose={() => { handleClearSelection(); setCalendarOpen(false); }}
                title={t("Create Holiday Calendar")}
                maxWidth="lg"
                fullWidth
                PaperProps={{ sx: { minHeight: '80vh' } }}
                customActions={
                    <Box sx={{ display: "flex", width: "100%", alignItems: "center" }}>
                        <Button variant="outlined" color="error" onClick={handleClearSelection}>
                            {t("Clear Selection")}
                        </Button>
                        <Box sx={{ flexGrow: 1 }} />
                        <Button
                            onClick={() => { handleClearSelection(); setCalendarOpen(false); }}
                            variant="outlined"
                            sx={{ borderColor: "#3092e3", color: "#3092e3", "&:hover": { borderColor: "#1a6fb5", color: "#1a6fb5", backgroundColor: "rgba(48,146,227,0.08)" } }}
                        >
                            {t("Cancel")}
                        </Button>
                        <Button
                            variant="contained"
                            sx={{ ml: 1, "&:hover": { bgcolor: "#1976d2" } }}
                            onClick={handleOk}
                            disabled={selectedDates.length === 0 && deselectedExistingDates.length === 0}
                        >
                            {t("OK")}
                        </Button>
                    </Box>
                }
            >
                    <Grid container spacing={3} sx={{ mb: 4, mt: 1 }}>
                        <Grid item xs={12} md={3}>
                            <TextField
                                label={t("Session")}
                                value={selectedSession?.name || ""}
                                fullWidth
                                disabled
                                variant="outlined"
                                size="small"
                            />
                        </Grid>
                        <Grid item xs={12} md={2}>
                            <FormControl fullWidth size="small">
                                <InputLabel>{t("Year")}</InputLabel>
                                <Select
                                    value={currentYear}
                                    onChange={(e) => setCurrentYear(e.target.value as number)}
                                    label={t("Year")}
                                >
                                    {sessionYears.map(y => (
                                        <MenuItem key={y} value={y}>{y}</MenuItem>
                                    ))}
                                </Select>
                            </FormControl>
                        </Grid>
                        <Grid item xs={12} md={3}>
                            <FormControl fullWidth size="small">
                                <InputLabel>{t("Month")}</InputLabel>
                                <Select
                                    value={currentMonth}
                                    onChange={(e) => setCurrentMonth(e.target.value as number)}
                                    label={t("Month")}
                                >
                                    {[...Array(12)].map((_, i) => (
                                        <MenuItem key={i} value={i}>
                                            {format(new Date(2024, i, 1), "MMMM")}
                                        </MenuItem>
                                    ))}
                                </Select>
                            </FormControl>
                        </Grid>
                        <Grid item xs={12} md={4}>
                            <FormControl fullWidth size="small">
                                <InputLabel>{t("Selection Mode")}</InputLabel>
                                <Select
                                    value={selectionMode}
                                    onChange={(e) => {
                                        const newMode = e.target.value as "single" | "range";
                                        if (selectedDates.length > 0) {
                                            setPendingSelectionMode(newMode);
                                            setConfirmModeChangeOpen(true);
                                        } else {
                                            setSelectionMode(newMode);
                                        }
                                    }}
                                    label={t("Selection Mode")}
                                >
                                    <MenuItem value="single">{t("Single Date Selection")}</MenuItem>
                                    <MenuItem value="range">{t("Range Date Selection")}</MenuItem>
                                </Select>
                            </FormControl>
                        </Grid>
                    </Grid>

                    <Box sx={{ mb: 2 }}>
                        <Box display="flex" justifyContent="space-between" alignItems="center" sx={{ mb: 2 }}>
                            <Typography variant="h6" sx={{ fontWeight: 600 }}>
                                {format(new Date(currentYear, currentMonth), "MMMM yyyy")}
                            </Typography>
                            <Box>
                                <IconButton onClick={() => {
                                    if (currentMonth === 0) {
                                        setCurrentMonth(11);
                                        setCurrentYear(prev => prev - 1);
                                    } else {
                                        setCurrentMonth(prev => prev - 1);
                                    }
                                }}>
                                    <ChevronLeftIcon />
                                </IconButton>
                                <IconButton onClick={() => {
                                    if (currentMonth === 11) {
                                        setCurrentMonth(0);
                                        setCurrentYear(prev => prev + 1);
                                    } else {
                                        setCurrentMonth(prev => prev + 1);
                                    }
                                }}>
                                    <ChevronRightIcon />
                                </IconButton>
                            </Box>
                        </Box>

                        <Grid container sx={{ border: "1px solid #e0e0e0", borderRadius: 2, overflow: "hidden" }}>
                            {['Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat', 'Sun'].map(day => (
                                <Grid item xs={1.714} key={day} sx={{
                                    bgcolor: "#f5f5f5",
                                    py: 1.5,
                                    textAlign: "center",
                                    fontWeight: "bold",
                                    borderBottom: "1px solid #e0e0e0",
                                    display: "flex",
                                    alignItems: "center",
                                    justifyContent: "center",
                                    color: day === 'Sun' ? 'error.main' : 'inherit'
                                }}>
                                    {day}
                                </Grid>
                            ))}
                            {Array.from({ length: (startOfMonth(new Date(currentYear, currentMonth)).getDay() + 6) % 7 }).map((_, i) => (
                                <Grid item xs={1.714} key={`empty-${i}`} sx={{
                                    borderRight: "1px solid #e0e0e0",
                                    borderBottom: "1px solid #e0e0e0",
                                    height: 100
                                }} />
                            ))}
                            {daysInMonth.map(day => (
                                (() => {
                                    const sunday = isSunday(day);
                                    const existing = isExistingHoliday(day);
                                    const selected = isDateSelected(day);
                                    const disabled = sunday;
                                    return (
                                <Grid item xs={1.714} key={day.toString()}
                                    onClick={() => {
                                        if (disabled) return;
                                        handleDateClick(day);
                                    }}
                                    sx={{
                                        borderRight: "1px solid #e0e0e0",
                                        borderBottom: "1px solid #e0e0e0",
                                        height: 100,
                                        p: 1,
                                        cursor: disabled ? "not-allowed" : "pointer",
                                        transition: "all 0.2s",
                                        position: "relative",
                                        bgcolor: disabled
                                            ? "rgba(244, 67, 54, 0.06)"
                                            : selected
                                                ? "rgba(48, 146, 227, 0.15)"
                                                : (existing ? "rgba(76, 175, 80, 0.1)" : "white"),
                                        opacity: disabled ? 0.6 : 1,
                                        "&:hover": { bgcolor: disabled ? "rgba(244, 67, 54, 0.06)" : "rgba(0,0,0,0.03)" }
                                    }}
                                >
                                    <Typography variant="body1" sx={{
                                        fontWeight: (selected || existing) ? 700 : 400,
                                        color: disabled
                                            ? "error.main"
                                            : selected
                                                ? "#3092e3"
                                                : (existing ? "#4caf50" : "inherit")
                                    }}>
                                        {format(day, "d")}
                                    </Typography>
                                    {!disabled && (selected || existing) && (
                                        <Box sx={{
                                            position: "absolute",
                                            bottom: 8,
                                            right: 8,
                                            width: 8,
                                            height: 8,
                                            borderRadius: "50%",
                                            bgcolor: selected ? "#3092e3" : "#4caf50"
                                        }} title={existing ? useholidaysQueryBySession.data?.find(h => {
                                            const normalizedDateStr = format(day, "dd-MM-yyyy");
                                            return h.dates.includes(normalizedDateStr);
                                        })?.description : ""} />
                                    )}
                                </Grid>
                                    );
                                })()
                            ))}
                        </Grid>
                    </Box>
            </CommonModal>

            {/* Holiday Details Dialog */}
            <CommonModal
                open={dialogOpen}
                onClose={() => { setDialogOpen(false); setHolidaySubmitError(""); }}
                title={t("Holiday Details")}
                maxWidth="md"
                fullWidth
                onSubmit={handleSubmitHoliday}
                submitLabel={t("Submit")}
                cancelLabel={t("Cancel")}
                submitDisabled={!holidayType || !description || createHolidayMutation.isLoading}
                loading={createHolidayMutation.isLoading}
            >
                    <Box sx={{ mt: 1 }}>
                        {holidaySubmitError ? (
                            <Typography sx={{ mb: 2 }} color="error">
                                {holidaySubmitError}
                            </Typography>
                        ) : null}
                        <Typography variant="subtitle2" color="textSecondary" gutterBottom>
                            {(() => {
                                const existingHoliday = useholidaysQueryBySession.data?.find(h => h.id === editingHolidayId);
                                const remainingExisting = existingHoliday ? existingHoliday.dates.filter(d => !deselectedExistingDates.includes(d)) : [];
                                const newDatesFormatted = selectedDates.map(d => format(d, "MMM d"));
                                const allDates = [...remainingExisting.map(d => {
                                    const [day, month, year] = d.split("-").map(Number);
                                    return format(new Date(year, month - 1, day), "MMM d");
                                }), ...newDatesFormatted];
                                return `${new Set(allDates).size} ${t("dates selected")}`;
                            })()}
                        </Typography>
                        <Box sx={{ display: "flex", flexWrap: "wrap", gap: 0.5, mb: 3 }}>
                            {(() => {
                                const existingHoliday = useholidaysQueryBySession.data?.find(h => h.id === editingHolidayId);
                                const remainingExisting = existingHoliday ? existingHoliday.dates.filter(d => !deselectedExistingDates.includes(d)) : [];

                                // Union of dates formatted for display
                                const displayDates: { label: string, isNew: boolean, original: string | Date }[] = [
                                    ...remainingExisting.map(d => {
                                        const [day, month, year] = d.split("-").map(Number);
                                        return {
                                            label: format(new Date(year, month - 1, day), "MMM d"),
                                            isNew: false,
                                            original: d
                                        };
                                    }),
                                    ...selectedDates.map(d => ({
                                        label: format(d, "MMM d"),
                                        isNew: true,
                                        original: d
                                    }))
                                ];

                                // Filter out duplicates if any (e.g. somehow same date in both)
                                const uniqueDisplay = displayDates.filter((v, i, a) => a.findIndex(t => t.label === v.label) === i);

                                return uniqueDisplay.map((item, index) => (
                                    <Chip
                                        key={index}
                                        label={item.label}
                                        size="small"
                                        variant={item.isNew ? "filled" : "outlined"}
                                        color={item.isNew ? "primary" : "default"}
                                        onDelete={() => {
                                            if (item.isNew) {
                                                setSelectedDates(selectedDates.filter(date => !isSameDay(date, item.original as Date)));
                                            } else {
                                                setDeselectedExistingDates([...deselectedExistingDates, item.original as string]);
                                            }
                                        }}
                                    />
                                ));
                            })()}
                        </Box>

                        <Grid container spacing={3}>
                            <Grid item xs={12} md={6}>
                                <TextField
                                    select
                                    fullWidth
                                    label={t("Type of Holiday")}
                                    value={holidayType}
                                    onChange={(e) => setHolidayType(Number(e.target.value))}
                                    variant="outlined"
                                    size="small"
                                >
                                    {activeHolidayTypesQuery.data?.map(option => (
                                        <MenuItem key={option.id} value={option.id}>{formatHolidayType(option.name)}</MenuItem>
                                    ))}
                                </TextField>
                            </Grid>
                            <Grid item xs={12} md={6}>
                                <FormControl fullWidth size="small">
                                    <InputLabel>{t("Class Option")}</InputLabel>
                                    <Select
                                        value={classOption}
                                        onChange={(e) => setClassOption(e.target.value)}
                                        label={t("Class Option")}
                                    >
                                        <MenuItem value="all">{t("For All Classes")}</MenuItem>
                                        <MenuItem value="particular">{t("For Particular Class")}</MenuItem>
                                    </Select>
                                </FormControl>
                            </Grid>

                            {classOption === "particular" && (
                                <Grid item xs={12}>
                                    <FormControl fullWidth size="small">
                                        <InputLabel>{t("Select Classes")}</InputLabel>
                                        <Select
                                            multiple
                                            value={selectedClasses}
                                            onChange={handleClassChange}
                                            input={<OutlinedInput label={t("Select Classes")} />}
                                            MenuProps={{
                                                PaperProps: {
                                                    style: {
                                                        maxHeight: 300,
                                                        width: 250,
                                                    },
                                                },
                                            }}
                                            renderValue={(selected) => (
                                                <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5 }}>
                                                    {selected.map((value) => (
                                                        <Chip
                                                            key={value}
                                                            label={classesQuery.data?.find(c => c.id === value)?.name || value}
                                                            size="small"
                                                        />
                                                    ))}
                                                </Box>
                                            )}
                                        >
                                            {classesQuery.data && classesQuery.data.length > 0 && (
                                                <MenuItem value="all">
                                                    <Checkbox
                                                        checked={selectedClasses.length === classesQuery.data.length}
                                                        indeterminate={selectedClasses.length > 0 && selectedClasses.length < classesQuery.data.length}
                                                    />
                                                    <ListItemText primary={t("Select All")} />
                                                </MenuItem>
                                            )}
                                            {classesQuery.data?.map((cls) => (
                                                <MenuItem key={cls.id} value={cls.id}>
                                                    <Checkbox checked={selectedClasses.indexOf(cls.id!) > -1} />
                                                    <ListItemText primary={`${cls.name} - ${cls.section}`} />
                                                </MenuItem>
                                            ))}
                                        </Select>
                                    </FormControl>
                                </Grid>
                            )}

                            <Grid item xs={12}>
                                <TextField
                                    fullWidth
                                    multiline
                                    rows={3}
                                    label={t("Holiday Description")}
                                    value={description}
                                    onChange={(e) => setDescription(e.target.value)}
                                    placeholder={t("Enter details about the holiday...")}
                                />
                            </Grid>
                        </Grid>
                    </Box>
            </CommonModal>

            {/* Mode Change Confirmation Dialog */}
            <CommonModal
                open={confirmModeChangeOpen}
                onClose={() => setConfirmModeChangeOpen(false)}
                title={t("Confirmation")}
                onSubmit={() => {
                    if (pendingSelectionMode) {
                        setSelectionMode(pendingSelectionMode);
                    }
                    setSelectedDates([]);
                    setRangeStart(null);
                    setRangeEnd(null);
                    setConfirmModeChangeOpen(false);
                    setPendingSelectionMode(null);
                }}
                submitLabel={t("Confirm")}
                cancelLabel={t("Cancel")}
            >
                <Typography>
                    {t("This action may lose your previous selected dates for holiday")}
                </Typography>
            </CommonModal>
        </Paper>
    );
};

export default HolidayCalendarManagement;